import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Block,
  History,
  PersonRemove,
  PersonOff,
  Add,
  School,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { branchAPI, courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const BranchEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branch, setBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    district: '',
    pincode: '',
    contactPersonName: '',
    contactPersonEmail: '',
    contactPersonPhone: '',
  });

  // Dialog states
  const [adminHistoryDialog, setAdminHistoryDialog] = useState(false);
  const [changeAdminDialog, setChangeAdminDialog] = useState(false);
  const [removeAdminDialog, setRemoveAdminDialog] = useState(false);
  const [blockAdminDialog, setBlockAdminDialog] = useState(false);
  const [blockBranchDialog, setBlockBranchDialog] = useState(false);
  const [assignCourseDialog, setAssignCourseDialog] = useState(false);
  const [removeCourseDialog, setRemoveCourseDialog] = useState(false);

  // Data states
  const [adminHistory, setAdminHistory] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseRemovalReason, setCourseRemovalReason] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Change admin form
  const [newAdminData, setNewAdminData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const districts = [
    'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
    'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
    'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod'
  ];

  useEffect(() => {
    fetchBranch();
    fetchAvailableCourses();
  }, [id]);

  const fetchBranch = async () => {
    try {
      setLoading(true);
      const response = await branchAPI.getOne(id);
      const branchData = response.data.data?.branch || response.data.data;
      setBranch(branchData);

      setFormData({
        name: branchData.name || '',
        street: branchData.address?.street || '',
        city: branchData.address?.city || '',
        district: branchData.address?.district || '',
        pincode: branchData.address?.pincode || '',
        contactPersonName: branchData.contactPerson?.name || '',
        contactPersonEmail: branchData.contactPerson?.email || '',
        contactPersonPhone: branchData.contactPerson?.phone || '',
      });
    } catch (error) {
      console.error('Error fetching branch:', error);
      toast.error('Failed to load branch details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCourses = async () => {
    try {
      const response = await courseAPI.getAll();
      setAvailableCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchAdminHistory = async () => {
    try {
      const response = await branchAPI.getAdminHistory(id);
      setAdminHistory(response.data.data || []);
      setAdminHistoryDialog(true);
    } catch (error) {
      console.error('Error fetching admin history:', error);
      toast.error('Failed to load admin history');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.district) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        address: {
          street: formData.street,
          city: formData.city,
          district: formData.district,
          state: 'Kerala',
          pincode: formData.pincode,
        },
        contactPerson: {
          name: formData.contactPersonName,
          email: formData.contactPersonEmail,
          phone: formData.contactPersonPhone,
        },
      };

      await branchAPI.update(id, payload);
      toast.success('Branch updated successfully');
      fetchBranch();
    } catch (error) {
      console.error('Error updating branch:', error);
      toast.error(error.response?.data?.message || 'Failed to update branch');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeAdmin = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newAdminData.name || !newAdminData.email) {
      toast.error('Admin name and email are required');
      return;
    }
    if (!emailRegex.test(newAdminData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      await branchAPI.changeAdmin(id, newAdminData);
      toast.success('Admin changed successfully. Credentials sent via email.');
      setChangeAdminDialog(false);
      setNewAdminData({ name: '', email: '', phone: '' });
      fetchBranch();
    } catch (error) {
      console.error('Error changing admin:', error);
      toast.error(error.response?.data?.message || 'Failed to change admin');
    }
  };

  const handleRemoveAdmin = async () => {
    try {
      await branchAPI.changeAdmin(id, { removeAdmin: true });
      toast.success('Admin removed from branch');
      setRemoveAdminDialog(false);
      fetchBranch();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error(error.response?.data?.message || 'Failed to remove admin');
    }
  };

  const handleBlockAdmin = async () => {
    if (!branch?.admin?._id) {
      toast.error('No admin assigned to this branch');
      return;
    }

    try {
      // This would require a user block endpoint
      // For now, we'll use the changeAdmin endpoint to block
      toast.info('Admin blocking feature - contact system administrator');
      setBlockAdminDialog(false);
    } catch (error) {
      console.error('Error blocking admin:', error);
      toast.error('Failed to block admin');
    }
  };

  const handleToggleBranchBlock = async () => {
    try {
      await branchAPI.toggleBlock(id, blockReason);
      toast.success(
        branch?.status === 'blocked'
          ? 'Branch unblocked successfully'
          : 'Branch blocked successfully'
      );
      setBlockBranchDialog(false);
      setBlockReason('');
      fetchBranch();
    } catch (error) {
      console.error('Error toggling branch block:', error);
      toast.error('Failed to update branch status');
    }
  };

  const handleAssignCourse = async () => {
    if (!selectedCourses || selectedCourses.length === 0) {
      toast.error('Please select at least one course');
      return;
    }

    try {
      // Assign multiple courses
      for (const courseId of selectedCourses) {
        await branchAPI.assignCourse(id, courseId);
      }
      toast.success(`${selectedCourses.length} course(s) assigned successfully`);
      setAssignCourseDialog(false);
      setSelectedCourses([]);
      fetchBranch();
    } catch (error) {
      console.error('Error assigning course:', error);
      toast.error(error.response?.data?.message || 'Failed to assign course');
    }
  };

  const handleRemoveCourse = async () => {
    if (!selectedCourse) return;

    try {
      await branchAPI.removeCourse(id, selectedCourse, courseRemovalReason);
      toast.success('Course removed. No new enrollments will be accepted.');
      setRemoveCourseDialog(false);
      setSelectedCourse(null);
      setCourseRemovalReason('');
      fetchBranch();
    } catch (error) {
      console.error('Error removing course:', error);
      toast.error(error.response?.data?.message || 'Failed to remove course');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!branch) {
    return (
      <Box>
        <Alert severity="error">Branch not found</Alert>
      </Box>
    );
  }

  const activeCourses = branch.courseAssignments?.filter(c => c.status === 'active') || [];
  const inactiveCourses = branch.courseAssignments?.filter(c => c.status === 'inactive') || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Edit Branch / ATC Center
          </Typography>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mt: 1 }}>
            Code: {branch.code}
          </Typography>
        </Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/branches')}
        >
          Back to Branches
        </Button>
      </Box>

      {/* Branch Status */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Branch Status
            </Typography>
            <Chip
              label={branch.status?.toUpperCase()}
              color={
                branch.status === 'approved' ? 'success' :
                branch.status === 'blocked' ? 'error' :
                branch.status === 'pending' ? 'warning' : 'default'
              }
              size="medium"
            />
          </Box>
          {isSuperAdmin && (
            <Button
              variant={branch.status === 'blocked' ? 'contained' : 'outlined'}
              color={branch.status === 'blocked' ? 'success' : 'error'}
              startIcon={<Block />}
              onClick={() => setBlockBranchDialog(true)}
            >
              {branch.status === 'blocked' ? 'Unblock Branch' : 'Block Branch'}
            </Button>
          )}
        </Box>
        {branch.status === 'blocked' && branch.blockReason && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Block Reason:</strong> {branch.blockReason}
          </Alert>
        )}
      </Paper>

      {/* Branch Details Form */}
      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Branch Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Branch Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Branch Address
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Street Address"
                name="street"
                value={formData.street}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="City/Town"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="District"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
                fullWidth
                sx={{ minWidth: 250 }}
              >
                {districts.map((district) => (
                  <MenuItem key={district} value={district}>
                    {district}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                fullWidth
                inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Contact Person
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Person Name"
                name="contactPersonName"
                value={formData.contactPersonName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Email"
                name="contactPersonEmail"
                type="email"
                value={formData.contactPersonEmail}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Phone"
                name="contactPersonPhone"
                value={formData.contactPersonPhone}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/branches')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Admin Management */}
      {isSuperAdmin && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Branch Administrator
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {branch.admin ? (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {branch.admin.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {branch.admin.email}
                </Typography>
                {branch.admin.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {branch.admin.phone}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<PersonRemove />}
                  onClick={() => setRemoveAdminDialog(true)}
                  color="warning"
                >
                  Remove Admin
                </Button>
                <Button
                  size="small"
                  startIcon={<PersonOff />}
                  onClick={() => setBlockAdminDialog(true)}
                  color="error"
                >
                  Block Admin
                </Button>
              </CardActions>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No admin assigned to this branch
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => setChangeAdminDialog(true)}
            >
              Change Branch Admin
            </Button>
            <Button
              variant="outlined"
              startIcon={<History />}
              onClick={fetchAdminHistory}
            >
              View Admin History
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Course Management */}
      {isSuperAdmin && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Course Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAssignCourseDialog(true)}
            >
              Assign Course
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {activeCourses.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Active Courses
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {activeCourses.map((assignment) => (
                  <Grid item xs={12} md={6} key={assignment._id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <School color="primary" />
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {assignment.course?.name || 'Course'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => {
                            setSelectedCourse(assignment.course._id);
                            setRemoveCourseDialog(true);
                          }}
                        >
                          Remove Course
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No active courses assigned to this branch
            </Alert>
          )}

          {inactiveCourses.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                Inactive Courses (No new enrollments)
              </Typography>
              <Grid container spacing={2}>
                {inactiveCourses.map((assignment) => (
                  <Grid item xs={12} md={6} key={assignment._id}>
                    <Card variant="outlined" sx={{ opacity: 0.7 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <School color="disabled" />
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {assignment.course?.name || 'Course'}
                          </Typography>
                          <Chip label="Inactive" size="small" color="default" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Removed: {new Date(assignment.inactivatedAt).toLocaleDateString()}
                        </Typography>
                        {assignment.inactivationReason && (
                          <Typography variant="caption" color="text.secondary">
                            Reason: {assignment.inactivationReason}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Admin History Dialog */}
      <Dialog
        open={adminHistoryDialog}
        onClose={() => setAdminHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Branch Admin History</DialogTitle>
        <DialogContent>
          {adminHistory.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admin Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Assigned Date</TableCell>
                    <TableCell>Removed Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminHistory.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell>{record.admin?.name}</TableCell>
                      <TableCell>{record.admin?.email}</TableCell>
                      <TableCell>
                        {new Date(record.assignedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {record.removedAt
                          ? new Date(record.removedAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          size="small"
                          color={record.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No admin history available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Change Admin Dialog */}
      <Dialog
        open={changeAdminDialog}
        onClose={() => setChangeAdminDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Branch Admin</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
            The current admin will be automatically blocked and credentials will be sent to the new admin via email.
          </Alert>
          <TextField
            label="New Admin Name"
            value={newAdminData.name}
            onChange={(e) =>
              setNewAdminData({ ...newAdminData, name: e.target.value })
            }
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="New Admin Email"
            type="email"
            value={newAdminData.email}
            onChange={(e) =>
              setNewAdminData({ ...newAdminData, email: e.target.value })
            }
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="New Admin Phone"
            value={newAdminData.phone}
            onChange={(e) =>
              setNewAdminData({ ...newAdminData, phone: e.target.value })
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeAdminDialog(false)}>Cancel</Button>
          <Button onClick={handleChangeAdmin} variant="contained" color="primary">
            Change Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Admin Dialog */}
      <Dialog
        open={removeAdminDialog}
        onClose={() => setRemoveAdminDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>Remove Branch Admin</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Are you sure you want to remove the current admin from this branch? The branch will have no admin until you assign a new one.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveAdminDialog(false)}>Cancel</Button>
          <Button onClick={handleRemoveAdmin} variant="contained" color="warning">
            Remove Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Admin Dialog */}
      <Dialog
        open={blockAdminDialog}
        onClose={() => setBlockAdminDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>Block Admin Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            This will block the admin's account system-wide. They will not be able to access any branches. Contact system administrator for this action.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockAdminDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Block/Unblock Branch Dialog */}
      <Dialog
        open={blockBranchDialog}
        onClose={() => setBlockBranchDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {branch?.status === 'blocked' ? 'Unblock Branch' : 'Block Branch'}
        </DialogTitle>
        <DialogContent>
          {branch?.status !== 'blocked' && (
            <TextField
              label="Block Reason"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              sx={{ mt: 2 }}
            />
          )}
          {branch?.status === 'blocked' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will unblock the branch and allow normal operations.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockBranchDialog(false)}>Cancel</Button>
          <Button
            onClick={handleToggleBranchBlock}
            variant="contained"
            color={branch?.status === 'blocked' ? 'success' : 'error'}
          >
            {branch?.status === 'blocked' ? 'Unblock' : 'Block'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Course Dialog */}
      <Dialog
        open={assignCourseDialog}
        onClose={() => setAssignCourseDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Courses to Branch</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, minWidth: 250 }}>
            <InputLabel>Select Courses</InputLabel>
            <Select
              multiple
              value={selectedCourses}
              onChange={(e) => setSelectedCourses(e.target.value)}
              input={<OutlinedInput label="Select Courses" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((courseId) => {
                    const course = availableCourses.find(c => c._id === courseId);
                    return (
                      <Chip key={courseId} label={course?.name} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {availableCourses.map((course) => (
                <MenuItem key={course._id} value={course._id}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            You can select multiple courses at once
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignCourseDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignCourse} variant="contained" color="primary">
            Assign {selectedCourses.length > 0 ? `(${selectedCourses.length})` : ''} Course(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Course Dialog */}
      <Dialog
        open={removeCourseDialog}
        onClose={() => setRemoveCourseDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Course from Branch</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            Removing a course will prevent new student enrollments, but existing students can continue their studies.
          </Alert>
          <TextField
            label="Removal Reason (Optional)"
            value={courseRemovalReason}
            onChange={(e) => setCourseRemovalReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveCourseDialog(false)}>Cancel</Button>
          <Button onClick={handleRemoveCourse} variant="contained" color="error">
            Remove Course
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchEdit;
